import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type {
  ImChannelProactivityExperiment,
  ImChannelProactivityExperimentKind,
  ProactivenessRollout,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import {
  WEEKDAY_LABELS,
  WEEKDAYS,
} from '@/features/im-channel-proactivity-experiments/weekdays';

type ExperimentEditorFieldsProps = {
  mode: 'create' | 'duplicate' | 'edit';
  experiment?: ImChannelProactivityExperiment;
  rollouts: ProactivenessRollout[];
};

const COMMON_INTEGRATIONS = [
  { id: 'gmail', label: 'Gmail' },
  { id: 'googledrive', label: 'Google Drive' },
  { id: 'mcp:linear', label: 'Linear' },
  { id: 'mcp:notion', label: 'Notion' },
];

const WATCHER_CONDITION_TEMPLATE =
  'Only notify me when [condition]. If it is not met, write the report as usual but do not notify me about this run. If you could not check the condition, notify me.';

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** Renders editor fields and generates a slug until the operator overrides it. */
export const ExperimentEditorFields = ({
  mode,
  experiment,
  rollouts,
}: ExperimentEditorFieldsProps) => {
  const editing = mode === 'edit';
  const [slug, setSlug] = useState(experiment?.slug ?? '');
  const [slugEdited, setSlugEdited] = useState(mode !== 'create');
  const [kind, setKind] = useState<ImChannelProactivityExperimentKind>(
    experiment?.kind ?? 'watcher'
  );

  const handleTitleChange = (title: string) => {
    if (!slugEdited) setSlug(slugify(title));
  };
  const handleSlugChange = (nextSlug: string) => {
    setSlug(nextSlug.toLowerCase());
    setSlugEdited(true);
  };

  return (
    <>
      <section className="space-y-4">
        <label className="block space-y-1.5" htmlFor="experiment-rollout">
          <span className="text-sm font-medium">Rollout</span>
          <Select
            id="experiment-rollout"
            name="rollout_slug"
            defaultValue={experiment?.rollout_slug ?? rollouts[0]?.slug}
            disabled={editing}
            required
          >
            {rollouts.map((rollout) => (
              <option key={rollout.slug} value={rollout.slug}>
                {rollout.slug} - {rollout.section} ({rollout.flag_key})
              </option>
            ))}
          </Select>
          <span className="block text-xs text-muted-foreground">
            Select one rollout to set the section and PostHog flag for this
            routine. Several routines can share a rollout.
          </span>
        </label>
        <label className="block space-y-1.5" htmlFor="experiment-title">
          <span className="text-sm font-medium">Name</span>
          <Input
            id="experiment-title"
            name="title"
            defaultValue={experiment?.title}
            onChange={(event) => handleTitleChange(event.target.value)}
            placeholder="Top of mind"
            autoFocus
            required
          />
        </label>

        <label className="block space-y-1.5" htmlFor="experiment-slug">
          <span className="text-sm font-medium">Stable ID</span>
          <Input
            id="experiment-slug"
            name="slug"
            value={slug}
            onChange={(event) => handleSlugChange(event.target.value)}
            placeholder="top_of_mind"
            pattern="[a-z0-9]+(?:[-_][a-z0-9]+)*"
            disabled={editing}
            required
          />
          <span className="block text-xs text-muted-foreground">
            Lowercase letters, numbers, dashes, and underscores. The stable ID
            cannot be renamed.
          </span>
        </label>

        <label className="block space-y-1.5" htmlFor="experiment-description">
          <span className="text-sm font-medium">Description</span>
          <Input
            id="experiment-description"
            name="description"
            defaultValue={experiment?.description}
            placeholder="What this experiment is trying to help with"
          />
        </label>

        <label className="block space-y-1.5" htmlFor="experiment-prompt">
          <span className="text-sm font-medium">Full agent prompt</span>
          <Textarea
            id="experiment-prompt"
            name="prompt"
            defaultValue={experiment?.prompt}
            placeholder="Define what to look for, how to judge it, and what the report must contain."
            className="min-h-40"
            required
          />
          <span className="block text-xs text-muted-foreground">
            Runs as the routine&apos;s instructions. The channel agent reads the
            report and messages the user in its own voice.
          </span>
        </label>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">Eligible integrations</legend>
          <div className="flex flex-wrap gap-2">
            {COMMON_INTEGRATIONS.map(({ id, label }) => (
              <label
                key={id}
                className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm has-[:checked]:border-foreground"
              >
                <input
                  type="checkbox"
                  name="required_integrations"
                  value={id}
                  defaultChecked={
                    experiment?.required_integrations.includes(id) ?? false
                  }
                />
                {label}
              </label>
            ))}
          </div>
          <label
            className="block text-sm font-medium"
            htmlFor="experiment-integrations"
          >
            Other integrations
          </label>
          <Input
            id="experiment-integrations"
            name="other_required_integrations"
            aria-describedby="experiment-integrations-help"
            defaultValue={experiment?.required_integrations
              .filter(
                (integration) =>
                  !COMMON_INTEGRATIONS.some(({ id }) => id === integration)
              )
              .join(', ')}
            placeholder="Integration IDs, separated by commas"
          />
          <p
            id="experiment-integrations-help"
            className="text-xs text-muted-foreground"
          >
            Enter comma-separated{' '}
            <a
              href="https://github.com/littlebirdai/littlebird/blob/main/backend/lb/features/integrations/composio/composio_config.py"
              target="_blank"
              rel="noreferrer noopener"
              className="underline"
            >
              Composio toolkit names
            </a>{' '}
            with an active auth config, or <code>mcp:&lt;id&gt;</code> from the{' '}
            <a
              href="https://github.com/littlebirdai/littlebird/blob/main/backend/lb/features/integrations/remote_mcp/remote_mcp_catalog.py"
              target="_blank"
              rel="noreferrer noopener"
              className="underline"
            >
              remote MCP catalog
            </a>
            . Each user needs an enabled connection for at least one selected or
            listed integration. Leave this empty to allow users without one.
          </p>
        </fieldset>

        <label className="block space-y-1.5" htmlFor="experiment-kind">
          <span className="text-sm font-medium">Delivery</span>
          <Select
            id="experiment-kind"
            name="kind"
            value={kind}
            onChange={(event) =>
              setKind(event.target.value as ImChannelProactivityExperimentKind)
            }
          >
            <option value="watcher">Watcher</option>
            <option value="digest">Digest</option>
          </Select>
          {kind === 'watcher' ? (
            <span className="block text-xs text-muted-foreground">
              The run stays silent only if the prompt states the condition in
              these terms, so include a sentence like: &ldquo;
              {WATCHER_CONDITION_TEMPLATE}&rdquo;
            </span>
          ) : (
            <span className="block text-xs text-muted-foreground">
              Every run messages the user, including runs that found nothing.
            </span>
          )}
        </label>
      </section>

      <label className="block space-y-1.5" htmlFor="experiment-schedule">
        <span className="text-sm font-medium">Schedule</span>
        <Input
          id="experiment-schedule"
          name="schedule_times"
          defaultValue={experiment?.schedule_times.join(', ') ?? '09:00'}
          placeholder="08:30, 13:30, 18:30"
          pattern="(?:[01]\d|2[0-3]):[0-5]\d(?:\s*,\s*(?:[01]\d|2[0-3]):[0-5]\d)*"
          required
        />
        <span className="block text-xs text-muted-foreground">
          Comma-separated times in each user&apos;s timezone.
        </span>
      </label>

      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Days</legend>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((day) => (
            <label
              key={day}
              className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm has-[:checked]:border-foreground"
            >
              <input
                type="checkbox"
                name="weekdays"
                value={day}
                defaultChecked={experiment?.weekdays.includes(day) ?? false}
              />
              {WEEKDAY_LABELS[day]}
            </label>
          ))}
        </div>
        <span className="block text-xs text-muted-foreground">
          Leave every day unchecked to run daily. Weekly schedules with several
          times need the times to share the same minutes.
        </span>
      </fieldset>
    </>
  );
};
